import { InfoCircleTwoTone } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  TimePicker,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import {
  DAYS,
  MODULES_KEY,
  PERMISSION_OBJECT,
  REGEX,
  TIME_FORMAT,
  TIME_KEYS,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  TIME_CONFIGURATION,
  UPDATE_MONTHLY_INSTANCES,
} from './graphql/Mutations';
import { GET_TIME_CONFIGURATION, MONTHLY_INSTANCES } from './graphql/Queries';

function Configuration() {
  const [form] = Form?.useForm();
  const [configForm] = Form?.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [timeConfigTableData, setTimeConfigTableData] = useState([]);
  const [isSaved, setIsSaved] = useState(true);
  const [isTimeConfigSaved, setIsTimeConfigSaved] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [timeConfigModal, setTimeConfigModal] = useState(false);
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.CONFIGURATIONS,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });

  const [executeQuery, { data: monthlyInstancesData }] = useLazyQuery(
    MONTHLY_INSTANCES,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [getTimeConfigQuery, { data: timeConfigData }] = useLazyQuery(
    GET_TIME_CONFIGURATION,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [editMonthlyInstances] = useMutation(UPDATE_MONTHLY_INSTANCES, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      executeQuery();
    },
    onError() {},
  });

  const [timeConfig] = useMutation(TIME_CONFIGURATION, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      getTimeConfigQuery();
    },
    onError() {},
  });

  useEffect(() => {
    executeQuery();
    getTimeConfigQuery();
  }, []);

  useEffect(() => {
    if (monthlyInstancesData) {
      const initialData = monthlyInstancesData?.monthlyInstances?.data?.map(
        (item) => ({
          id: item?.id,
          levelId: item?.level?.id,
          level: item?.level?.name,
          lateComing: item?.lateComing === null ? '-' : item?.lateComing,
          workFromHome: item?.workFromHome === null ? '-' : item?.workFromHome,
          hoursAdjustment:
            item?.hoursAdjustment === null ? '-' : item?.hoursAdjustment,
          isReferral: item?.isReferral || false,
        }),
      );
      setTableData(initialData);
    }
  }, [monthlyInstancesData]);

  const handleInputChange = (value, record, dataIndex) => {
    setTableData((prevTableData) => {
      const updatedTableData = [...prevTableData];
      const recordIndex = updatedTableData?.findIndex(
        (item) => item?.id === record?.id,
      );
      const updatedRecord = { ...updatedTableData[recordIndex] };
      updatedRecord[dataIndex] = parseInt(value, 10);
      updatedTableData[recordIndex] = updatedRecord;
      return updatedTableData;
    });
    setIsSaved(false);
  };

  const handleSwitchChange = (checked, record) => {
    setTableData((prevTableData) => {
      const updatedTableData = [...prevTableData];
      const recordIndex = updatedTableData?.findIndex(
        (item) => item?.id === record?.id,
      );
      const updatedRecord = { ...updatedTableData[recordIndex] };
      updatedRecord.isReferral = checked;
      updatedTableData[recordIndex] = updatedRecord;
      return updatedTableData;
    });
    setIsSaved(false);
  };

  const handleSave = async () => {
    try {
      const selectedFields = tableData?.map(
        ({
          levelId,
          lateComing,
          workFromHome,
          hoursAdjustment,
          isReferral,
        }) => ({
          levelId,
          lateComing: lateComing === '-' ? null : lateComing,
          workFromHome: workFromHome === '-' ? null : workFromHome,
          hoursAdjustment: hoursAdjustment === '-' ? null : hoursAdjustment,
          isReferral,
        }),
      );
      await editMonthlyInstances({
        variables: {
          data: selectedFields,
        },
      });
      setIsSaved(true);
      setModalOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console?.error('Error:', error);
    }
  };

  const handleTimeSave = async () => {
    try {
      const timeConfigPayload = timeConfigTableData?.flatMap((row) =>
        row?.keys?.map((keyValue, i) => ({
          key: keyValue,
          value:
            keyValue === TIME_KEYS.HOUR_ADJUSTMENT_DAYS_LIMIT
              ? String(row?.values?.[i] ?? '')
              : row?.values?.[i],
        })),
      );
      await timeConfig({ variables: { data: timeConfigPayload } });

      setIsTimeConfigSaved(true);
      setTimeConfigModal(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error:', error);
    }
  };

  const handleCancel = () => {
    executeQuery();
    setIsSaved(true);
  };

  const handleTimeConfigCancel = () => {
    getTimeConfigQuery();
    setIsTimeConfigSaved(true);
  };

  const validateNumber = (_, value) => {
    if (REGEX?.NUMBER?.test(value) || REGEX?.ONLY_DASH?.test(value)) {
      return Promise.resolve();
    }
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject('Should be a valid Number');
  };

  const columns = [
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: '20%',
      align: 'center',
    },
    {
      title: 'Late coming',
      dataIndex: 'lateComing',
      key: 'lateComing',
      width: '20%',
      render: (text, record) => (
        <Form.Item
          name={`lateComing_${record?.id}`}
          rules={[
            {
              validator: validateNumber,
            },
          ]}
        >
          <Input
            defaultValue={record?.lateComing}
            onChange={(e) =>
              handleInputChange(e?.target?.value, record, 'lateComing')
            }
            onPressEnter={() => setModalOpen(true)}
            disabled={!isUpdatePermission}
          />
        </Form.Item>
      ),
    },
    {
      title: 'WFH',
      dataIndex: 'workFromHome',
      key: 'workFromHome',
      width: '20%',
      render: (text, record) => (
        <Form.Item
          name={`workFromHome_${record?.id}`}
          rules={[
            {
              validator: validateNumber,
            },
          ]}
        >
          <Input
            defaultValue={record?.workFromHome}
            onChange={(e) =>
              handleInputChange(e?.target?.value, record, 'workFromHome')
            }
            onPressEnter={() => setModalOpen(true)}
            disabled={!isUpdatePermission}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Hours Adjustment',
      dataIndex: 'hoursAdjustment',
      key: 'hoursAdjustment',
      width: '20%',
      render: (text, record) => (
        <Form.Item
          name={`hoursAdjustment_${record?.id}`}
          rules={[
            {
              validator: validateNumber,
            },
          ]}
        >
          <Input
            defaultValue={record?.hoursAdjustment}
            onChange={(e) =>
              handleInputChange(e?.target?.value, record, 'hoursAdjustment')
            }
            onPressEnter={() => setModalOpen(true)}
            disabled={!isUpdatePermission}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Referral Amount',
      dataIndex: 'isReferral',
      key: 'isReferral',
      align: 'center',
      render: (text, record) => (
        <Form.Item name={`isReferral_${record?.id}`} valuePropName="checked">
          <Switch
            defaultChecked={record?.isReferral}
            onChange={(checked) => handleSwitchChange(checked, record)}
            disabled={!isUpdatePermission}
          />
        </Form.Item>
      ),
    },
  ];

  const staticConfig = [
    // OFFICE START - END TIME
    {
      keys: [TIME_KEYS.OFFICE_START_TIME, TIME_KEYS.OFFICE_END_TIME],
      label: 'Office Timing',
      component: (row, onChange) => (
        <div className="d-flex gap-8">
          {/* OFFICE START TIME */}
          <Form.Item
            name={TIME_KEYS.OFFICE_START_TIME}
            rules={[{ required: true, message: 'Please select start time!' }]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              value={
                row?.values?.[0] ? dayjs(row?.values?.[0], TIME_FORMAT) : null
              }
              onChange={(time) =>
                onChange([
                  time ? time?.format(TIME_FORMAT) : null,
                  row?.values?.[1],
                ])
              }
              inputReadOnly
            />
          </Form.Item>

          {/* OFFICE END TIME */}
          <Form.Item
            name={TIME_KEYS.OFFICE_END_TIME}
            rules={[
              { required: true, message: 'Please select end time!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue(TIME_KEYS.OFFICE_START_TIME);
                  if (
                    !value ||
                    dayjs(value, TIME_FORMAT).isAfter(dayjs(start, TIME_FORMAT))
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      'Office end time must be after office start time!',
                    ),
                  );
                },
              }),
            ]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              value={
                row?.values?.[1] ? dayjs(row?.values?.[1], TIME_FORMAT) : null
              }
              onChange={(time) =>
                onChange([
                  row?.values?.[0],
                  time ? time?.format(TIME_FORMAT) : null,
                ])
              }
              inputReadOnly
            />
          </Form.Item>
        </div>
      ),
    },

    // LATE MARK TIME
    {
      keys: [TIME_KEYS.LATE_MARK_TIME],
      label: 'Late Mark Time',
      component: (row, onChange) => (
        <Form.Item
          name={TIME_KEYS.LATE_MARK_TIME}
          rules={[
            { required: true, message: 'Please select late mark time!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const officeStart = getFieldValue(TIME_KEYS.OFFICE_START_TIME);
                const maxCheckIn = getFieldValue(TIME_KEYS.MAX_CHECKIN_TIME);

                if (!value) return Promise.resolve();
                const val = dayjs(value, TIME_FORMAT);
                if (
                  officeStart &&
                  !val?.isAfter(dayjs(officeStart, TIME_FORMAT))
                ) {
                  return Promise.reject(
                    new Error(
                      'Late mark time must be after office start time!',
                    ),
                  );
                }
                if (
                  maxCheckIn &&
                  !val?.isBefore(dayjs(maxCheckIn, TIME_FORMAT))
                ) {
                  return Promise.reject(
                    new Error(
                      'Late mark time must be before max check-in time!',
                    ),
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <TimePicker
            format="HH:mm"
            minuteStep={15}
            value={
              row?.values?.[0] ? dayjs(row?.values?.[0], TIME_FORMAT) : null
            }
            onChange={(time) =>
              onChange([time ? time?.format(TIME_FORMAT) : null])
            }
            inputReadOnly
          />
        </Form.Item>
      ),
    },

    // MAX CHECK-IN TIME
    {
      keys: [TIME_KEYS.MAX_CHECKIN_TIME],
      label: 'Maximum Allowable Check-In Time',
      component: (row, onChange) => (
        <Form.Item
          name={TIME_KEYS.MAX_CHECKIN_TIME}
          rules={[
            { required: true, message: 'Please select max check-in time!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const officeStart = getFieldValue(TIME_KEYS.OFFICE_START_TIME);
                const officeEnd = getFieldValue(TIME_KEYS.OFFICE_END_TIME);
                const lateMark = getFieldValue(TIME_KEYS.LATE_MARK_TIME);

                if (!value) return Promise.resolve();
                const val = dayjs(value, TIME_FORMAT);

                if (
                  officeStart &&
                  val?.isBefore(dayjs(officeStart, TIME_FORMAT))
                ) {
                  return Promise.reject(
                    new Error(
                      'Max check-in cannot be before office start time!',
                    ),
                  );
                }

                if (officeEnd && val?.isAfter(dayjs(officeEnd, TIME_FORMAT))) {
                  return Promise.reject(
                    new Error(
                      'Max allowable check-in time must be after late mark time!',
                    ),
                  );
                }

                if (lateMark && !val?.isAfter(dayjs(lateMark, TIME_FORMAT))) {
                  return Promise.reject(
                    new Error('Max check-in must be after late mark time!'),
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <TimePicker
            format="HH:mm"
            minuteStep={15}
            value={
              row?.values?.[0] ? dayjs(row?.values?.[0], TIME_FORMAT) : null
            }
            onChange={(time) =>
              onChange([time ? time?.format(TIME_FORMAT) : null])
            }
            inputReadOnly
          />
        </Form.Item>
      ),
    },

    // LUNCH BREAK START - END
    {
      keys: [TIME_KEYS.LUNCH_START_TIME, TIME_KEYS.LUNCH_END_TIME],
      label: 'Lunch Break (Start - End)',
      component: (row, onChange) => (
        <div className="d-flex gap-8">
          <Form.Item
            name={TIME_KEYS.LUNCH_START_TIME}
            rules={[
              { required: true, message: 'Select lunch start time!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const officeStart = getFieldValue(
                    TIME_KEYS.OFFICE_START_TIME,
                  );
                  const officeEnd = getFieldValue(TIME_KEYS.OFFICE_END_TIME);
                  if (!value) return Promise.resolve();
                  const val = dayjs(value, TIME_FORMAT);

                  if (val?.isBefore(dayjs(officeStart, TIME_FORMAT))) {
                    return Promise.reject(
                      new Error('Cannot be before office start time!'),
                    );
                  }
                  if (val?.isAfter(dayjs(officeEnd, TIME_FORMAT))) {
                    return Promise.reject(
                      new Error('Cannot be after office end time!'),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              value={
                row?.values?.[0] ? dayjs(row?.values?.[0], TIME_FORMAT) : null
              }
              onChange={(time) =>
                onChange([
                  time ? time?.format(TIME_FORMAT) : null,
                  row?.values?.[1],
                ])
              }
              inputReadOnly
            />
          </Form.Item>

          <Form.Item
            name={TIME_KEYS.LUNCH_END_TIME}
            rules={[
              { required: true, message: 'Select lunch end time!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const lunchStart = getFieldValue(TIME_KEYS.LUNCH_START_TIME);
                  const officeEnd = getFieldValue(TIME_KEYS.OFFICE_END_TIME);
                  if (!value) return Promise.resolve();
                  const val = dayjs(value, TIME_FORMAT);

                  if (
                    lunchStart &&
                    !val?.isAfter(dayjs(lunchStart, TIME_FORMAT))
                  ) {
                    return Promise.reject(
                      new Error(
                        'Lunch end must be after office lunch time start!',
                      ),
                    );
                  }
                  if (
                    officeEnd &&
                    val?.isAfter(dayjs(officeEnd, TIME_FORMAT))
                  ) {
                    return Promise.reject(
                      new Error('Cannot be after office end time!'),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              value={
                row?.values?.[1] ? dayjs(row?.values?.[1], TIME_FORMAT) : null
              }
              onChange={(time) =>
                onChange([
                  row?.values?.[0],
                  time ? time?.format(TIME_FORMAT) : null,
                ])
              }
              inputReadOnly
            />
          </Form.Item>
        </div>
      ),
    },

    // MAX EXTENDED LUNCH TIME
    {
      keys: [TIME_KEYS.MAX_LUNCH_END_TIME],
      label: 'Max Time for Extended Lunch Break',
      component: (row, onChange) => (
        <Form.Item
          name={TIME_KEYS.MAX_LUNCH_END_TIME}
          rules={[
            { required: true, message: 'Please select max lunch end time!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const lunchEnd = getFieldValue(TIME_KEYS.LUNCH_END_TIME);
                const officeEnd = getFieldValue(TIME_KEYS.OFFICE_END_TIME);
                if (!value) return Promise.resolve();
                const val = dayjs(value, TIME_FORMAT);

                if (lunchEnd && !val?.isAfter(dayjs(lunchEnd, TIME_FORMAT))) {
                  return Promise.reject(
                    new Error(
                      'Max lunch end time must be after lunch end time!',
                    ),
                  );
                }

                if (officeEnd && val?.isAfter(dayjs(officeEnd, TIME_FORMAT))) {
                  return Promise.reject(
                    new Error(
                      'Max lunch end time must be before to office end time!',
                    ),
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <TimePicker
            format="HH:mm"
            minuteStep={15}
            value={
              row?.values?.[0] ? dayjs(row?.values?.[0], TIME_FORMAT) : null
            }
            onChange={(time) =>
              onChange([time ? time?.format(TIME_FORMAT) : null])
            }
            inputReadOnly
          />
        </Form.Item>
      ),
    },

    // LEAVING EARLY APPLY TIME
    {
      keys: [TIME_KEYS.LEAVING_EARLY_APPLY_TIME],
      label: 'Leaving Early Apply Time',
      component: (row, onChange) => (
        <Form.Item
          name={TIME_KEYS.LEAVING_EARLY_APPLY_TIME}
          rules={[
            { required: true, message: 'Please select early apply time!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const officeStart = getFieldValue(TIME_KEYS.OFFICE_START_TIME);
                const officeEnd = getFieldValue(TIME_KEYS.OFFICE_END_TIME);
                if (!value) return Promise.resolve();
                const val = dayjs(value, TIME_FORMAT);

                if (val?.isBefore(dayjs(officeStart, TIME_FORMAT))) {
                  return Promise.reject(
                    new Error('Cannot be before office start time!'),
                  );
                }

                if (val?.isAfter(dayjs(officeEnd, TIME_FORMAT))) {
                  return Promise.reject(
                    new Error('Cannot be after office end time!'),
                  );
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <TimePicker
            format="HH:mm"
            minuteStep={15}
            value={
              row?.values?.[0] ? dayjs(row?.values?.[0], TIME_FORMAT) : null
            }
            onChange={(time) =>
              onChange([time ? time?.format(TIME_FORMAT) : null])
            }
            inputReadOnly
          />
        </Form.Item>
      ),
    },

    // HOUR ADJUSTMENT DAYS LIMIT
    {
      keys: [TIME_KEYS.HOUR_ADJUSTMENT_DAYS_LIMIT],
      label: 'Hour Adjustment Days Limit',
      component: (row, onChange) => (
        <Form.Item
          name={TIME_KEYS.HOUR_ADJUSTMENT_DAYS_LIMIT}
          rules={[
            {
              required: true,
              type: 'number',
              min: 1,
              message: 'Please enter a valid number of days!',
            },
            {
              validator: (_, value) => {
                if (value === null || value === undefined)
                  return Promise.resolve();
                if (!Number.isInteger(value)) {
                  return Promise.reject(
                    new Error('Please enter a valid days limit!'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            min={1}
            step={1}
            value={row?.values?.[0] ?? null}
            onChange={(val) => onChange([val ?? null])}
          />
        </Form.Item>
      ),
    },

    // WORKING DAYS
    {
      keys: [TIME_KEYS.WORKING_DAYS],
      label: 'Working Days',
      component: (row, onChange) => (
        <Form.Item
          name={TIME_KEYS.WORKING_DAYS}
          rules={[{ required: true, message: 'Please select working days!' }]}
        >
          <Select
            className="days-selection"
            mode="multiple"
            placeholder="Select working days"
            value={row?.values?.[0] ? row?.values?.[0]?.split(',') : []}
            onChange={(selected) => onChange([selected?.join(',')])}
            options={DAYS}
          />
        </Form.Item>
      ),
    },
  ];

  const TimeConfigcolumns = [
    {
      title: 'Label',
      width: '50%',
      dataIndex: 'label',
      key: 'label',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: <div className="d-flex justify-center">Value</div>,
      width: '50%',
      dataIndex: 'values',
      key: 'value',
      align: 'left',
      render: (_, row) => {
        const cfg = staticConfig?.find(
          (c) => c?.keys?.join('__') === row?.rowId,
        );
        return cfg?.component(row, (newValues) => {
          setTimeConfigTableData((prev) =>
            prev?.map((rowKey) =>
              rowKey?.rowId === row?.rowId
                ? { ...rowKey, values: newValues }
                : rowKey,
            ),
          );
          setIsTimeConfigSaved(false);
        });
      },
    },
  ];

  useEffect(() => {
    if (!timeConfigData) return;

    const mappedData = staticConfig?.map((cfg) => {
      const values = cfg?.keys?.map((keyValue) => {
        const configValue =
          timeConfigData?.getTimeConfigurations?.find(
            (timeKey) => timeKey?.key === keyValue,
          )?.value ?? null;

        if (
          keyValue === TIME_KEYS.HOUR_ADJUSTMENT_DAYS_LIMIT &&
          configValue != null
        ) {
          return String(configValue);
        }
        if (keyValue === TIME_KEYS.WORKING_DAYS && configValue != null) {
          return configValue;
        }
        return configValue;
      });

      return {
        rowId: cfg?.keys.join('__'),
        keys: cfg?.keys,
        label: cfg?.label,
        values,
      };
    });

    setTimeConfigTableData(mappedData);

    const formValues = {};
    mappedData?.forEach((row) => {
      row?.keys?.forEach((key, idx) => {
        const val = row?.values?.[idx];
        if (!val) {
          formValues[key] = null;
        } else if (key === TIME_KEYS.HOUR_ADJUSTMENT_DAYS_LIMIT) {
          formValues[key] = Number(val);
        } else if (key === TIME_KEYS.WORKING_DAYS) {
          formValues[key] = val?.split(',');
        } else {
          formValues[key] = dayjs(val, TIME_FORMAT);
        }
      });
    });

    configForm.setFieldsValue(formValues);
  }, [timeConfigData, configForm]);

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title={
          <div className="d-flex justify-between">
            <div>Configure</div>
          </div>
        }
      >
        <div className="card-body-wrapper">
          <Row>
            <Col xs={24} sm={12} md={12} lg={12}>
              <div className="mr-16 ml-16">
                <div className="monthly-instances-alert">
                  <div className="heading-config-text">
                    Maximum Monthly Instances
                    <Tooltip title="Please note that updates will be displayed here and will be applicable from next month.">
                      <span className="ml-4 mt-4">
                        <InfoCircleTwoTone />
                      </span>
                    </Tooltip>
                  </div>
                  <div>
                    <div className="monthly-instances-btn">
                      <Button onClick={handleCancel} disabled={isSaved}>
                        Cancel
                      </Button>
                      <Button
                        className="save-btn"
                        type="primary"
                        onClick={() => setModalOpen(true)}
                        disabled={isSaved}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
                <Form form={form} className="configuration-form">
                  <CommonTable
                    size="small"
                    className="monthly-instances-table"
                    columns={columns}
                    dataSource={tableData}
                    rowKey={(obj) => obj?.id}
                    bordered
                  />
                </Form>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <div className="mr-16 ml-16">
                <div className="monthly-instances-alert">
                  <div className="heading-config-text">
                    Timing Configuration
                  </div>
                  <div>
                    <div className="monthly-instances-btn">
                      <Button
                        onClick={handleTimeConfigCancel}
                        disabled={isTimeConfigSaved}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="save-btn"
                        type="primary"
                        onClick={() => setTimeConfigModal(true)}
                        disabled={isTimeConfigSaved || !isFormValid}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
                <Form
                  form={configForm}
                  onFieldsChange={() => {
                    const hasErrors = configForm
                      ?.getFieldsError()
                      ?.some(
                        ({ errors: fieldErrors }) => fieldErrors?.length > 0,
                      );
                    setIsFormValid(!hasErrors);
                  }}
                >
                  <CommonTable
                    size="small"
                    className="monthly-instances-table"
                    dataSource={timeConfigTableData}
                    columns={TimeConfigcolumns}
                    bordered
                  />
                </Form>
              </div>
            </Col>
          </Row>
        </div>
      </Card>
      {modalOpen && (
        <Modal
          title="Attention User!"
          centered
          closable={false}
          open={modalOpen}
          onOk={handleSave}
          onCancel={() => setModalOpen(false)}
        >
          <Alert
            description="Updates will only be applicable from the next month. Do you still wish to proceed?"
            type="info"
            showIcon
          />
        </Modal>
      )}

      {timeConfigModal && (
        <Modal
          title="Attention User!"
          centered
          closable={false}
          open={timeConfigModal}
          onOk={handleTimeSave}
          onCancel={() => setTimeConfigModal(false)}
        >
          <Alert
            description="Updates will be applicable from today onwards. Do you still wish to proceed?"
            type="info"
            showIcon
          />
        </Modal>
      )}
    </div>
  );
}

export default Configuration;
